import React, { useRef, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { FiUploadCloud, FiX, FiChevronLeft, FiChevronRight, FiFileText } from "react-icons/fi";

const DOC_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const isDoc = (file) => DOC_TYPES.includes(file.type);

const KIND_ACCEPT = { image: "image/*", video: "video/*", doc: "application/pdf,.doc,.docx" };
const KIND_LABEL = { image: "images", video: "videos", doc: "documents" };
const KIND_CHECK = {
  image: (f) => f.type.startsWith("image/"),
  video: (f) => f.type.startsWith("video/"),
  doc: isDoc,
};

const BROKEN_IMAGE =
  "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='90'%3E%3Crect width='120' height='90' fill='%23f1f5f9'/%3E%3Ctext x='60' y='50' font-size='11' text-anchor='middle' fill='%2394a3b8' font-family='sans-serif'%3EImage unavailable%3C/text%3E%3C/svg%3E";

/**
 * Drag-and-drop uploader. Files are sent with the same multipart calls as before:
 * images/videos -> /all/upload/files/images, PDFs -> /all/upload/files/attachments (field "files").
 *
 * items:      [{ id, kind: 'image' | 'video' | 'pdf', url, name, index }] already uploaded, `index` is the
 *             position inside the consumer's own list for that kind
 * onUploaded: ({ media: rawServerItems[], attachments: rawServerItems[] }) => void
 * onRemove:   (item) => void
 * onMove:     (item, direction -1 | 1) => void
 * kinds:      which file kinds this zone takes (default all): any of 'image' | 'video' | 'doc'
 * maxSizeMb:  per-file limit (property media 5 MB and post media 50 MB, as on mobile)
 * items also carry `isLast` (last position inside their own kind)
 */
const MediaUploader = ({
  items,
  onUploaded,
  onRemove,
  onMove,
  maxSizeMb = 5,
  kinds = ["image", "video", "doc"],
  label = "Upload images, videos or PDFs",
}) => {
  const accept = kinds.map((k) => KIND_ACCEPT[k]).join(",");
  const kindsText = kinds.map((k) => KIND_LABEL[k]).join(", ");
  const isAllowedType = (file) => kinds.some((k) => KIND_CHECK[k](file));
  const maxBytes = maxSizeMb * 1024 * 1024;
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const user = useSelector((state) => state.auth.user);
  const token = user?.refreshToken || user?.token;

  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [problems, setProblems] = useState([]);
  const [failedFiles, setFailedFiles] = useState([]);

  const postFiles = async (files, path, share) => {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    return axios.post(`${baseUrl}${path}`, formData, {
      headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
      onUploadProgress: (e) => {
        if (e.total) setProgress(Math.round(share.base + (e.loaded / e.total) * share.span));
      },
    });
  };

  const upload = async (files) => {
    const media = files.filter((f) => f.type.startsWith("image/") || f.type.startsWith("video/"));
    const pdfs = files.filter(isDoc);
    const span = media.length && pdfs.length ? 50 : 100;
    setUploading(true);
    setProgress(0);
    setFailedFiles([]);
    try {
      const result = { media: [], attachments: [] };
      if (media.length) {
        const res = await postFiles(media, "/all/upload/files/images", { base: 0, span });
        result.media = res.data.propertyImages || [];
      }
      if (pdfs.length) {
        const res = await postFiles(pdfs, "/all/upload/files/attachments", { base: media.length ? 50 : 0, span });
        result.attachments = res.data.propertyAttachments || [];
      }
      onUploaded(result);
      setProgress(100);
    } catch (err) {
      setFailedFiles(files);
      setProblems([`Upload failed${err?.message ? ` (${err.message})` : ""}. Your files are kept — retry below.`]);
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const issues = [];
    const valid = [];
    files.forEach((file) => {
      if (!isAllowedType(file)) {
        issues.push(`${file.name}: unsupported file type (${kindsText} only)`);
      } else if (file.size > maxBytes) {
        issues.push(`${file.name}: ${(file.size / (1024 * 1024)).toFixed(2)} MB exceeds the ${maxSizeMb} MB limit`);
      } else {
        valid.push(file);
      }
    });
    setProblems(issues);
    if (valid.length) upload(valid);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      inputRef.current?.click();
    }
  };

  const renderThumb = (item) => {
    if (item.kind === "image") return <img
          className="fk-media__thumb"
          src={item.url}
          alt={item.name || "Uploaded"}
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = BROKEN_IMAGE;
          }}
        />;
    if (item.kind === "video") return <video className="fk-media__thumb" src={item.url} muted />;
    return (
      <div className="fk-media__thumb">
        <FiFileText size={30} aria-hidden="true" />
      </div>
    );
  };

  return (
    <div>
      <div
        className={`fk-drop ${dragOver ? "fk-drop--over" : ""}`}
        role="button"
        tabIndex={0}
        aria-label={label}
        onClick={() => inputRef.current?.click()}
        onKeyDown={onKeyDown}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
      >
        <FiUploadCloud size={34} color="#2563eb" aria-hidden="true" />
        <p className="fk-drop__title">{label}</p>
        <p className="fk-drop__sub">Drag &amp; drop or click to browse · {kindsText} · max {maxSizeMb} MB each</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={accept}
          hidden
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {uploading && (
        <div
          className="fk-progress"
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Upload progress"
        >
          <div className="fk-progress__bar" style={{ width: `${progress}%` }} />
        </div>
      )}

      {problems.length > 0 && (
        <div className="fk-alert" role="alert">
          {problems.map((p) => (
            <div key={p}>{p}</div>
          ))}
          {failedFiles.length > 0 && !uploading && (
            <button type="button" className="btn btn-sm btn-outline-danger mt-2" onClick={() => upload(failedFiles)}>
              Retry upload
            </button>
          )}
        </div>
      )}

      {items.length > 0 && (
        <div className="fk-media-grid">
          {items.map((item) => (
            <div className="fk-media" key={item.id}>
              {renderThumb(item)}
              <div className="fk-media__tools">
                {item.kind !== "pdf" && (
                  <>
                    <button
                      type="button"
                      className="fk-icon-btn"
                      aria-label={`Move ${item.name || "file"} earlier`}
                      disabled={item.index === 0}
                      onClick={() => onMove(item, -1)}
                    >
                      <FiChevronLeft size={14} />
                    </button>
                    <button
                      type="button"
                      className="fk-icon-btn"
                      aria-label={`Move ${item.name || "file"} later`}
                      disabled={item.isLast}
                      onClick={() => onMove(item, 1)}
                    >
                      <FiChevronRight size={14} />
                    </button>
                  </>
                )}
                <button type="button" className="fk-icon-btn" aria-label={`Remove ${item.name || "file"}`} onClick={() => onRemove(item)}>
                  <FiX size={14} />
                </button>
              </div>
              <p className="fk-media__name" title={item.name}>
                {item.name}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaUploader;
